import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminChats.css';
import DashboardTable from './DashboardTable';
import { API_URL } from '../../environment';

interface ClienteOption {
  cliente: string;
  id_time: number;
}

interface ClienteDashboardAPI {
  id_sdr: number;
  cliente: string;
}

interface ClienteDashboard {
  id: number;
  cliente: string;
}


interface Contact {
  fone: string;
  nome: string | null;
  mensagens?: number | string; // <-- novo campo
  registrado?: boolean;
}



interface Message {
  id: number;
  mensagem: string;
  status: 'send' | 'received';
  timestamp: string;
}

const AUTH_FLAG = 'isAdminAuthenticated';

export default function AdminChats() {
  const navigate = useNavigate();
  const [showDashboard, setShowDashboard] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(AUTH_FLAG) !== 'true') {
      navigate('/entrar');
    }
  }, [navigate]);

  const [clientes, setClientes] = useState<ClienteOption[]>([]);
  const [clientesDashboard, setClientesDashboard] = useState<ClienteDashboard[]>([]);
  const [selectedCliente, setSelectedCliente] = useState<ClienteOption | null>(null);
  const [selectedClienteDash, setSelectedClienteDash] = useState<ClienteDashboard | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const lastMessageIdRef = useRef<number>(0);
  const contactFonesRef = useRef<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [mostrarTodosContatos, setMostrarTodosContatos] = useState(false);




  const formatDate = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');

  const groupedMessages = messages.reduce<Record<string, Message[]>>((acc, msg) => {
    const day = formatDate(msg.timestamp);
    if (!acc[day]) acc[day] = [];
    acc[day].push(msg);
    return acc;
  }, {});

  useEffect(() => {
    fetch(`${API_URL}/admin/clientes`)
      .then(r => r.json())
      .then((data: ClienteOption[]) => {
        setClientes(data);
        if (data.length) setSelectedCliente(data[0]);
      })
      .catch(console.error);

    fetch(`${API_URL}/admin/sdr_clientes`)
      .then(r => r.json())
      .then((data: ClienteDashboardAPI[]) => {
        const formatado: ClienteDashboard[] = data.map(c => ({
          id: c.id_sdr,
          cliente: c.cliente,
        }));
        setClientesDashboard(formatado);
        if (formatado.length) setSelectedClienteDash(formatado[0]);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedCliente || showDashboard) return;
    fetch(`${API_URL}/admin/contacts?cliente=${encodeURIComponent(selectedCliente.cliente)}`)
      .then(r => r.json())
      .then((data: Contact[]) => {
        const unique = Array.from(new Map(data.map(c => [c.fone, c])).values());

        const enriched = unique.map(c => ({
          ...c,
          registrado: !!c.nome && c.nome.trim() !== '',
        }));

        setContacts(enriched);
        contactFonesRef.current = new Set(enriched.map(c => c.fone));
        setSelectedContact(null);
        setMessages([]);
      })
      .catch(console.error);
  }, [selectedCliente, showDashboard]);

  useEffect(() => {
    if (!selectedContact || !selectedCliente || showDashboard) return;
    fetch(
      `${API_URL}/admin/messages?cliente=${encodeURIComponent(selectedCliente.cliente)}&fone=${selectedContact.fone}`
    )
      .then(r => r.json())
      .then((data: Message[]) => {
        setMessages(data);
        if (data.length > 0) {
          lastMessageIdRef.current = Math.max(...data.map(m => m.id));
        }
      })
      .catch(console.error);
  }, [selectedContact, selectedCliente, showDashboard]);

  useEffect(() => {
    if (!selectedCliente || showDashboard) return;

    const interval = setInterval(() => {
      // Buscar mensagens se um contato estiver selecionado
      if (selectedContact) {
        fetch(
          `${API_URL}/admin/messages?cliente=${encodeURIComponent(selectedCliente.cliente)}&fone=${selectedContact.fone}`
        )
          .then(r => r.json())
          .then((data: Message[]) => {
            const novas = data.filter(m => m.id > lastMessageIdRef.current);
            if (novas.length > 0) {
              lastMessageIdRef.current = Math.max(...data.map(m => m.id));
              setMessages(prev => [...prev, ...novas]);
            }
          })
          .catch(console.error);
      }

      // Buscar novos contatos
      fetch(`${API_URL}/admin/contacts?cliente=${encodeURIComponent(selectedCliente.cliente)}`)
        .then(r => r.json())
        .then((data: Contact[]) => {
          const unicos = Array.from(new Map(data.map(c => [c.fone, c])).values());

          const novos = unicos.filter(c => !contactFonesRef.current.has(c.fone));
          if (novos.length > 0) {
            novos.forEach(c => contactFonesRef.current.add(c.fone));
            setContacts(prev => [...prev, ...novos]);
          }
        })
        .catch(console.error);

    }, 5000); // a cada 5 segundos

    return () => clearInterval(interval);
  }, [selectedCliente, selectedContact, showDashboard]);

  useEffect(() => {
    console.log("Contatos carregados:", contacts);
  }, [contacts]);



  return (
    <div className="admin-chats">
      <aside className="contact-list">
        <div className="filter">
          <label>Selecione Cliente:</label>
          {!showDashboard ? (
            <select
              value={selectedCliente?.cliente || ''}
              onChange={e => {
                const found = clientes.find(cli => cli.cliente === e.target.value);
                if (found) setSelectedCliente(found);
              }}
            >
              {clientes.map(cli => (
                <option key={cli.id_time} value={cli.cliente}>{cli.cliente}</option>
              ))}
            </select>
          ) : (
            <select
              value={selectedClienteDash?.cliente || ''}
              onChange={e => {
                const found = clientesDashboard.find(cli => cli.cliente === e.target.value);
                if (found) setSelectedClienteDash(found);
              }}
            >
              {clientesDashboard.map(cli => (
                <option key={cli.cliente} value={cli.cliente}>{cli.cliente}</option>
              ))}
            </select>
          )}

          <button
            style={{ marginTop: '0.5rem' }}
            onClick={() => setShowDashboard(prev => !prev)}
          >
            {showDashboard ? 'Ver Chats' : 'Ver Dashboard'}
          </button>

          <select
            className="form-select form-select-sm w-auto"
            value={mostrarTodosContatos ? "todos" : "mensagens"}
            onChange={(e) => setMostrarTodosContatos(e.target.value === "todos")}
            style={{ marginTop: '0.5rem' }}
          >
            <option value="mensagens">Com mensagens</option>
            <option value="todos">Todos</option>
          </select>

          <input
            type="text"
            placeholder="Buscar por número"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginTop: '0.5rem', width: '100%' }}
          />

        </div>

        {!showDashboard && (
          <div className="cards">
            {contacts
              .filter(c => {
                const totalMensagens = Number(c.mensagens ?? 0);
                const passouFiltroMensagens = mostrarTodosContatos || totalMensagens > 0;
                const passouBusca = String(c.fone).includes(searchTerm) || (c.nome?.toLowerCase().includes(searchTerm.toLowerCase()));
                return passouFiltroMensagens && passouBusca;
              })
              .map(c => (
                <div
                  key={c.fone}
                  className={`contact-card ${selectedContact?.fone === c.fone ? 'active' : ''} ${c.registrado ? 'registered' : ''}`}
                  onClick={() => setSelectedContact(c)}
                >
                  <div className="contact-name">{c.nome}</div>
                  <div className="contact-fone">{c.fone}</div>
                </div>
            ))}
            {contacts.length === 0 && (
              <p className="no-results">Nenhum contato encontrado.</p>
            )}
          </div>
        )}
      </aside>

      <section className="chat-window">
        {showDashboard ? (
          <div className="dashboard-wrapper">
            <h3 style={{ marginBottom: '1rem' }}>
              Dashboard para <strong>{selectedClienteDash?.cliente || '...'}</strong>
            </h3>
            {selectedClienteDash && (
              <DashboardTable
                key={selectedClienteDash.id}
                clientes={[selectedClienteDash]}
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
              />
            )}
          </div>
        ) : !selectedContact ? (
          <div className="placeholder">
            Selecione um contato para ver as mensagens
          </div>
        ) : (
          <>
            <header className="chat-header">
              Conversa com <strong>{selectedContact.nome}</strong>
            </header>
            <div className="messages-container">
              {Object.entries(groupedMessages).map(([date, msgs]) => (
                <div key={date} className="date-group">
                  <div className="date-separator">{date}</div>
                  {msgs.map(m => (
                    <div
                      key={m.id}
                      className={`message ${m.status === 'received' ? 'received' : 'sent'}`}
                    >
                      {m.mensagem}
                      <div className="ts">
                        {new Date(m.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
